import { useState, useEffect } from 'react';
import { usePage, Link } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import Helmet from 'react-helmet';
import {
  Container,
  Text,
  Button,
  Tabs,
  Group,
  Image,
  Title,
  Paper,
  Badge,
  Tooltip,
  Menu,
  ActionIcon,
  CopyButton,
  Box,
  Card,
  Modal,
  Grid,
  Avatar,
  Divider,
  MantineProvider,
  Spoiler,
  Stack,
} from '@mantine/core';
import { StablePagination } from '../../components/StablePagination';
import { useMediaQuery } from '@mantine/hooks';
import { Notifications, showNotification } from '@mantine/notifications';
import { useUser } from '../../src/context/UserContext';
import {
  IconCheck,
  IconDots,
  IconGlobe,
  IconBrandFacebook,
  IconBrandInstagram,
  IconClipboard,
  IconWorld,
  IconUsersGroup,
  IconCalendarDown,
  IconCalendarUp,
  IconCalendarEvent,
  IconVideo,
  IconX,
  IconChevronRight,
  IconMapPin,
} from '@tabler/icons-react';
import EventCard from '../../components/Cards/EventCard';
import ArtistCard from '../../components/Cards/ArtistCard';
import ManageBadge from '../../components/ManageBadge';
import FullCalendarView from '../../components/FullCalendarView';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import ArtistDrawerContent from '../../components/Drawer/ArtistDrawerContent';
import LoginModal from '../../components/LoginModal';
import useLoginModal from '../../hooks/useLoginModal';

import '../../styles/ShowView.css';
import '../../styles/OrganizerShowView.css';

// Interfaces
interface User {
  id: number;
  roles?: string[];
  subscribed_organizers?: Organizer[];
  [key: string]: any;
}

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  venue_name: string;
  address: string;
  city: string;
  state: string;
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  event_types?: { id: number; name: string }[];
  [key: string]: any;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  description?: string;
  avatar_url?: string;
  [key: string]: any;
}

interface Video {
  id: number;
  title: string;
  description: string;
  thumbnail_url: string;
  embed_url: string;
  video_type?: string;
  event?: Event;
  organizers?: Organizer[];
  artists?: Artist[];
  dance_styles?: any[];
}

interface Organizer {
  id: number;
  name: string;
  slug: string;
  area?: {
    id: number;
    name: string;
    slug: string;
    strip_area_suffix: boolean;
  };
  description: string;
  website_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  organization_image_url?: string;
  organizer_status_id: number;
  artists: Artist[];
  subscribed_users_count: number;
  videos: Video[];
  [key: string]: any;
  upcoming_events_count: number;
  past_events_count: number;
}

interface OrganizerShowProps extends PageProps {
  organizer: Organizer;
  upcoming_events: Event[];
  past_events: Event[];
  all_calendar_events: Event[];
  current_user: User | null;
  upcoming_events_count: number;
  past_events_count: number;
  is_subscribed: boolean;
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
}

export default function OrganizerShow() {
  interface CalendarEvent {
    id: string;
    title: string;
    start: string;
    end: string;
    url: string;
    backgroundColor: string;
    borderColor: string;
  }

  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  const {
    organizer,
    upcoming_events,
    past_events,
    current_user,
    all_calendar_events,
    upcoming_events_count,
    past_events_count,
    page_meta,
    is_subscribed: initialIsSubscribed,
  } = usePage<OrganizerShowProps>().props;
  const { user } = useUser() as { user: User | null; refreshUser: () => Promise<void> };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }

    console.log('✅✅✅ Org Area:', organizer.area);
    console.log('✅✅✅ Org Area:', organizer.area?.slug);
    console.log('Org Image URL:', organizer.organization_image_url);

    // console.log('Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    // Format the all_calendar_events for FullCalendar
    const formattedEvents = all_calendar_events.map((event) => ({
      id: event.id?.toString() || `event-${Math.random().toString(36).substr(2, 9)}`,
      title: event.name || 'Untitled Event',
      start: event.event_start_date,
      end: event.event_end_date,
      url: event.slug ? `/events/${event.slug}` : '#',
      backgroundColor: '#ea580c',
      borderColor: '#ea580c',
    }));

    setCalendarEvents(formattedEvents);
  }, [all_calendar_events]);

  // Load FullCalendar CSS
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/main.min.css';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // States
  const [activeTab, setActiveTab] = useState<string | null>('upcoming');
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [isSubscribed, setIsSubscribed] = useState(initialIsSubscribed);
  const [subscribersCount, setSubscribersCount] = useState(organizer.subscribed_users_count);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);

  // Drawer states
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<'event' | 'artist'>('event');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

  const ITEMS_PER_PAGE = 10;

  const areaName = organizer.area
    ? organizer.area.strip_area_suffix
      ? // remove trailing “Area” (with optional preceding space), case‑insensitive
        organizer.area.name.replace(/\s*\bArea\b\s*$/i, '')
      : organizer.area.name
    : '';

  console.log('Area Name:', areaName);

  // Calculate paginated events
  const paginatedUpcomingEvents = upcoming_events.slice(
    (upcomingPage - 1) * ITEMS_PER_PAGE,
    upcomingPage * ITEMS_PER_PAGE,
  );

  const paginatedPastEvents = past_events.slice((pastPage - 1) * ITEMS_PER_PAGE, pastPage * ITEMS_PER_PAGE);

  // Handle subscription action when user is authenticated
  const handleSubscriptionAuthenticated = async () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const endpoint = isSubscribed ? `/o/${organizer.slug}/unsubscribe` : `/o/${organizer.slug}/subscribe`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();

        // Update state based on server response
        setIsSubscribed(data.is_subscribed);
        setSubscribersCount(data.subscribers_count);

        // Show notification
        showNotification({
          color: 'green',
          title: data.is_subscribed ? 'Subscribed' : 'Unsubscribed',
          message: data.message,
          icon: <IconCheck />,
          autoClose: 3000,
        });
      } else {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Something went wrong',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error handling subscription:', error);
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Failed to process your request',
        icon: <IconX />,
        autoClose: 3000,
      });
    }
  };

  // Initialize the login modal hook
  const { isLoginModalOpen, loginModalProps, requireLogin, closeLoginModal } = useLoginModal({
    onAuthenticatedAction: handleSubscriptionAuthenticated,
  });

  // Handle video modal
  const openVideoModal = (video: Video) => {
    setCurrentVideo(video);
    setVideoModalOpen(true);
  };

  // Handle drawer functions
  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setDrawerContentType('event');
    setDrawerOpened(true);
  };

  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setDrawerContentType('artist');
    setDrawerOpened(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpened(false);
  };

  const isMobile = useMediaQuery('(max-width: 768px)');
  const userHasOrganizerAccess = user && (user.roles?.includes('SuperAdmin') || organizer.users?.includes(user.id));

  // Render status badge
  const renderStatusBadge = () => {
    if (!user || (!organizer.users?.includes(user.id) && !user.roles?.includes('SuperAdmin'))) {
      return null;
    }

    if (organizer.organizer_status_id === 2) {
      // Pending
      return (
        <Badge color="yellow" size="lg" variant="light" className="mb-3" style={{ textTransform: 'none' }}>
          Organizer Status: Pending
        </Badge>
      );
    } else if (organizer.organizer_status_id === 1) {
      // Active
      return (
        <Badge color="green" size="lg" variant="light" className="mb-3" style={{ textTransform: 'none' }}>
          Organizer Status: Active
        </Badge>
      );
    }

    return null;
  };

  // Create content for different sections
  const statusBadgeContent = renderStatusBadge();
  const manageBadgeContent = (
    <ManageBadge entityType="organizer" entitySlug={organizer.slug} userHasAccess={userHasOrganizerAccess} />
  );

  const imageContent = (
    <>
      <Stack gap="sm">
        {organizer?.area && (
          <Link href={`/city/${organizer?.area?.slug}`} className="inline-flex" style={{ textDecoration: 'none' }}>
            <Button
              component="button"
              variant="filled"
              color="#EA580C"
              radius="md"
              size="xs"
              styles={{
                root: {
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginBottom: '4px',
                  width: 'fit-content',
                },
                leftIcon: { marginRight: 4 },
                rightIcon: { marginLeft: 4 },
                label: { fontSize: '0.875rem', fontWeight: 500 },
              }}
              leftSection={<IconMapPin size={14} />}
              rightSection={<IconChevronRight size={14} />}
            >
              Listed in {areaName}
            </Button>
          </Link>
        )}
        <Image
          src={organizer.organization_image_url || 'https://dancewise.s3.amazonaws.com/WCWD+ORG+PLACEHOLDER.png'}
          alt={organizer.name}
          radius="md"
          mb="0"
          style={{ height: 'auto' }}
          w="100%"
        />
      </Stack>
    </>
  );

  const titleContent = (
    <Title order={2} className="rubik" style={{ color: '#FFF7ED' }}>
      {organizer.name}
    </Title>
  );

  const subscribeButtonContent = (
    <Button
      variant={isSubscribed ? 'light' : 'filled'}
      onClick={() =>
        requireLogin({
          actionText: 'to subscribe',
          entityName: organizer.name,
        })
      }
      color="#EA580C"
      fullWidth
    >
      {isSubscribed ? 'Subscribed' : 'Subscribe'}
    </Button>
  );

  const socialLinksContent = (
    <Group mt="xs" mb="xs">
      {organizer.website_url && (
        <Tooltip label="Website">
          <ActionIcon variant="subtle" size="lg" component="a" href={organizer.website_url} target="_blank">
            <IconWorld size={20} color="orange" />
          </ActionIcon>
        </Tooltip>
      )}

      {organizer.facebook_url && (
        <Tooltip label="Facebook">
          <ActionIcon variant="subtle" size="lg" component="a" href={organizer.facebook_url} target="_blank">
            <IconBrandFacebook size={20} color="orange" />
          </ActionIcon>
        </Tooltip>
      )}

      {organizer.instagram_url && (
        <Tooltip label="Instagram">
          <ActionIcon variant="subtle" size="lg" component="a" href={organizer.instagram_url} target="_blank">
            <IconBrandInstagram size={20} color="orange" />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  const bioContent = (
    <>
      <Title
        size="lg"
        fw={700}
        mb="sm"
        style={{ color: '#FFF7ED', fontWeight: 'bold', marginBottom: 0 }}
        className="poppins"
      >
        Organizer Bio:
      </Title>
      <Spoiler mb="4rem" maxHeight={100} mt="0" showLabel="Read more" hideLabel="Show less" color="orange">
        <Text mt="xs" dangerouslySetInnerHTML={{ __html: organizer.description }} style={{ color: '#FFF7ED' }} />
      </Spoiler>
    </>
  );

  const tabsContent = (
    <Tabs value={activeTab} onChange={setActiveTab} mt="md" color="orange">
      <Tabs.List>
        <Tabs.Tab
          value="upcoming"
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          leftSection={<IconCalendarUp size={20} stroke={1.5} color="#FFF7ED" />}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {organizer.upcoming_events_count}
            </Badge>
          }
        >
          Upcoming Events
        </Tabs.Tab>

        <Tabs.Tab
          value="past"
          leftSection={<IconCalendarDown size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {organizer.past_events_count}
            </Badge>
          }
        >
          Past Events
        </Tabs.Tab>

        <Tabs.Tab
          value="artists"
          leftSection={<IconUsersGroup size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {organizer.artists.length}
            </Badge>
          }
        >
          Artists
        </Tabs.Tab>

        <Tabs.Tab
          value="calendar"
          leftSection={<IconCalendarEvent size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Calendar View
        </Tabs.Tab>

        <Tabs.Tab
          value="videos"
          leftSection={<IconVideo size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Videos
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="upcoming" pt="md">
        {organizer.upcoming_events_count > 0 ? (
          <>
            <Group my="sm">
              {organizer.upcoming_events_count > 10 && (
                <StablePagination
                  total={Math.ceil(organizer.upcoming_events_count / ITEMS_PER_PAGE)}
                  value={upcomingPage}
                  onChange={setUpcomingPage}
                  color="#ea580c"
                />
              )}
            </Group>

            <Grid gutter="8px">
              {paginatedUpcomingEvents.map((event) => (
                <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                  <EventCard event={event} onCardClick={handleEventClick} />
                </Grid.Col>
              ))}
            </Grid>

            {organizer.upcoming_events_count > 10 && (
              <Group my="sm">
                <StablePagination
                  total={Math.ceil(organizer.upcoming_events_count / ITEMS_PER_PAGE)}
                  value={upcomingPage}
                  onChange={setUpcomingPage}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text size="xl" fw={500} mt={3} style={{ color: '#FFF7ED', fontWeight: 'bold' }} ta="center">
            No upcoming events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="past" pt="md">
        {past_events.length > 0 ? (
          <>
            <Group my="sm">
              {past_events.length > ITEMS_PER_PAGE && (
                <StablePagination
                  total={Math.ceil(organizer.past_events_count / ITEMS_PER_PAGE)}
                  value={pastPage}
                  onChange={setPastPage}
                  mb="md"
                  color="#ea580c"
                />
              )}
            </Group>
            <Grid gutter="8px">
              {paginatedPastEvents
                .sort((a, b) => {
                  const dateA = new Date(a.date || a.start_date || 0).getTime();
                  const dateB = new Date(b.date || b.start_date || 0).getTime();
                  return dateB - dateA;
                })
                .map((event, index) => (
                  <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={`past-event-${event.id || index}`}>
                    <EventCard event={event} onCardClick={handleEventClick} />
                  </Grid.Col>
                ))}
            </Grid>

            {past_events.length > ITEMS_PER_PAGE && (
              <Group my="sm">
                <StablePagination
                  total={Math.ceil(organizer.past_events_count / ITEMS_PER_PAGE)}
                  value={pastPage}
                  onChange={setPastPage}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text ta="center" mt="md">
            No past events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="artists" pt="md">
        {organizer.artists.length > 0 ? (
          <Grid gutter="8px">
            {organizer.artists.map((artist) => (
              <Grid.Col span={{ base: 6, sm: 3, md: 3 }} key={artist.id}>
                <ArtistCard artist={artist} onCardClick={handleArtistClick} />
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Text ta="center" mt="md">
            No artists associated with this organizer
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="calendar" pt="md">
        <Box mt="md" style={{ height: 'auto' }}>
          <FullCalendarView
            entityType="organizer"
            entityId={organizer.id}
            entitySlug={organizer.slug}
            initialEvents={all_calendar_events}
            initialCalendarMetadata={{
              month: new Date().getMonth() + 1,
              year: new Date().getFullYear(),
            }}
            eventTypes={all_calendar_events.length > 0 ? all_calendar_events[0].event_types || [] : []}
            danceStyles={all_calendar_events.length > 0 ? all_calendar_events[0].dance_styles || [] : []}
            initialFilters={{}}
            onEventClick={handleEventClick}
            filterOptions={{
              name: true,
              danceStyles: true,
              eventTypes: true,
            }}
            currentEvents={all_calendar_events}
            upcomingEvents={upcoming_events}
            pastEvents={past_events}
          />
        </Box>
      </Tabs.Panel>

      <Tabs.Panel value="videos" pt="md">
        {organizer.videos && organizer.videos.length > 0 ? (
          <Grid gutter="8px">
            {organizer.videos.map((video) => (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={video.id}>
                <Card shadow="sm" p="lg" radius="md" withBorder>
                  <Card.Section
                    style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}
                  >
                    <Image
                      src={video.thumbnail_url}
                      height={160}
                      alt={video.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Card.Section>

                  <Title order={5} mt="md">
                    {video.title}
                  </Title>
                  <Text mt="xs" size="sm" lineClamp={2}>
                    {video.description}
                  </Text>

                  {video.video_type && (
                    <Text size="sm" mt="xs">
                      <strong>Video Type:</strong> {video.video_type}
                    </Text>
                  )}

                  {video.event && (
                    <Text size="sm" mt="xs">
                      <strong>Event:</strong> {video.event.name}
                    </Text>
                  )}

                  {video.organizers && video.organizers.length > 0 && (
                    <Text size="sm" mt="xs">
                      <strong>Organizers:</strong> {video.organizers.map((org) => org.name).join(', ')}
                    </Text>
                  )}

                  {video.artists && video.artists.length > 0 && (
                    <Text size="sm" mt="xs">
                      <strong>Artists:</strong> {video.artists.map((artist) => artist.name).join(', ')}
                    </Text>
                  )}

                  {video.dance_styles && video.dance_styles.length > 0 && (
                    <Text size="sm" mt="xs">
                      <strong>Dance Styles:</strong> {video.dance_styles.map((style) => style.name).join(', ')}
                    </Text>
                  )}

                  <Button fullWidth mt="md" onClick={() => openVideoModal(video)}>
                    Watch Video
                  </Button>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Text ta="center" mt="md" style={{ color: '#FFF7ED' }} size="xl">
            No videos available.
          </Text>
        )}
      </Tabs.Panel>
    </Tabs>
  );

  // Generate the appropriate drawer content based on type
  const renderDrawerContent = () => {
    if (drawerContentType === 'event' && selectedEvent) {
      return <EventDrawerContent event={selectedEvent} />;
    } else if (drawerContentType === 'artist' && selectedArtist) {
      return <ArtistDrawerContent artist={selectedArtist} />;
    }
    return null;
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Create a clean description without HTML tags for meta description
  const cleanDescription = organizer.description
    ? organizer.description.replace(/<[^>]*>?/gm, '')
    : `Learn more about ${organizer.name} and their events.`;

  // Limit description to ~160 characters for meta tags
  const metaDescription = cleanDescription.length > 160 ? cleanDescription.substring(0, 157) + '...' : cleanDescription;

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Container size="xl" mb="8rem" style={{ minHeight: '100vh' }} px={isMobile ? 0 : undefined}>
        <Notifications position="top-right" />

        <Paper
          p="sm"
          shadow="xs"
          mt="sm"
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
          pos="relative"
          w="100%"
        >
          {/* Two-column layout container */}
          <div className="organizer-container">
            {/* Mobile view - sections directly in container for ordering */}
            <div className="organizer-mobile">
              <div className="organizer-section organizer-status">{statusBadgeContent}</div>
              <div className="organizer-section organizer-manage">{manageBadgeContent}</div>
              <div className="organizer-section organizer-image">{imageContent}</div>
              <div className="organizer-section organizer-title">{titleContent}</div>
              <div className="organizer-section organizer-subscribe">{subscribeButtonContent}</div>
              <div className="organizer-section organizer-social">{socialLinksContent}</div>
              <div className="organizer-section organizer-bio">{bioContent}</div>
              <div className="organizer-section organizer-tabs">{tabsContent}</div>
            </div>

            {/* Desktop view - two column layout */}
            <div className="organizer-sidebar">
              <div className="organizer-section organizer-status">{statusBadgeContent}</div>
              <div className="organizer-section organizer-manage">{manageBadgeContent}</div>
              <div className="organizer-section organizer-image">{imageContent}</div>
              <div className="organizer-section organizer-title">{titleContent}</div>
              <div className="organizer-section organizer-subscribe">{subscribeButtonContent}</div>
              <div className="organizer-section organizer-social">{socialLinksContent}</div>
              <div className="organizer-section organizer-bio">{bioContent}</div>
            </div>

            <div className="organizer-main">
              <div className="organizer-section organizer-tabs">{tabsContent}</div>
            </div>
          </div>
        </Paper>

        {/* Video Modal */}
        <Modal
          opened={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          title={currentVideo?.title || 'Video'}
          size="xl"
          centered
        >
          {currentVideo && (
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={currentVideo.embed_url}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </Modal>

        {/* Login Modal */}
        <LoginModal
          opened={isLoginModalOpen}
          onClose={closeLoginModal}
          actionText={loginModalProps.actionText}
          entityName={loginModalProps.entityName}
          returnPath={loginModalProps.returnPath}
        />

        {/* Dynamic Drawer */}
        <DynamicDrawer
          opened={drawerOpened}
          size={isMobile ? '85%' : '25%'}
          type={drawerContentType}
          data={drawerContentType === 'event' ? selectedEvent : selectedArtist}
          onClose={handleCloseDrawer}
        />
      </Container>
    </MantineProvider>
  );
}
